import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type CastingMatch,
  type Foundry,
  type FoundryPart,
  type GrateStyle,
  type StructureDetail,
  type StructureType,
  type StructureWrite,
} from '../lib/api';
import { Button, Card, ErrorAlert, Field, NumberInput, Pill, Select, TextArea, TextInput } from '../lib/ui';
import { clearUnsavedChanges } from '../lib/UnsavedChanges';
import { StructurePhotosCard } from './StructurePhotosCard';
import { ShareStructureModal } from './ShareStructureModal';
import { AdminAgentNotesCard } from '../lib/AdminAgentNotesCard';

// Empty payload used by the "new structure" route — all dimension fields null.
const blankWrite: StructureWrite = {
  nickname: '',
  permanent: true,
  oilFiltration: false,
  extendedBottomHeavyDuty: false,
  isDraft: true,
};

export function StructureFormPage() {
  const params = useParams<{ id?: string }>();
  const id = params.id && params.id !== 'new' ? Number(params.id) : null;
  const isNew = id === null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['structure', id],
    queryFn: () => api.get<StructureDetail>(`/api/v1/structures/${id}`),
    enabled: !isNew,
  });

  const typesQuery = useQuery({
    queryKey: ['structure-types'],
    queryFn: () => api.get<StructureType[]>('/api/v1/structure-types'),
  });

  const initial = useMemo<StructureWrite>(() => {
    if (isNew || !detailQuery.data) return blankWrite;
    const d = detailQuery.data;
    return {
      nickname: d.nickname,
      permanent: d.permanent,
      oilFiltration: d.oilFiltration,
      structureTypeId: d.structureTypeId,
      grateStyleId: d.grateStyleId,
      grateDiameter: d.grateDiameter,
      frameClearOpeningDiameter: d.frameClearOpeningDiameter,
      grateSizeLeftToRight: d.grateSizeLeftToRight,
      grateSizeFrontToBack: d.grateSizeFrontToBack,
      clearOpeningLeftToRight: d.clearOpeningLeftToRight,
      clearOpeningFrontToBack: d.clearOpeningFrontToBack,
      grateSizeLongSpan: d.grateSizeLongSpan,
      grateSizeShortSpan: d.grateSizeShortSpan,
      clearOpeningLongSpan: d.clearOpeningLongSpan,
      clearOpeningShortSpan: d.clearOpeningShortSpan,
      curbOpeningWidth: d.curbOpeningWidth,
      catchBasinWallToWallInsideDimension: d.catchBasinWallToWallInsideDimension,
      basinSize: d.basinSize,
      extendedBottomHeavyDuty: d.extendedBottomHeavyDuty,
      extendedBottomDepth: d.extendedBottomDepth,
      extendedBottomWidth: d.extendedBottomWidth,
      foundryId: d.foundryId,
      foundryPartId: d.foundryPartId,
      structureNotes: d.structureNotes,
      isDraft: d.isDraft,
    };
  }, [detailQuery.data, isNew]);

  return isNew || detailQuery.data ? (
    <StructureForm
      key={id ?? 'new'}
      initial={initial}
      structureTypes={typesQuery.data ?? []}
      detail={detailQuery.data ?? null}
      onSaved={(saved) => {
        clearUnsavedChanges();
        queryClient.invalidateQueries({ queryKey: ['structures'] });
        queryClient.invalidateQueries({ queryKey: ['structure', saved.id] });
        navigate(`/admin/structures/${saved.id}`);
      }}
      onDelete={async () => {
        if (id === null) return;
        if (!confirm('Delete this structure?')) return;
        clearUnsavedChanges();
        await api.del(`/api/v1/structures/${id}`);
        queryClient.invalidateQueries({ queryKey: ['structures'] });
        navigate('/admin/structures');
      }}
    />
  ) : (
    <Shell><p className="text-muted">Loading…</p></Shell>
  );
}

function StructureForm({
  initial,
  structureTypes,
  detail,
  onSaved,
  onDelete,
}: {
  initial: StructureWrite;
  structureTypes: StructureType[];
  detail: StructureDetail | null;
  onSaved: (saved: StructureDetail) => void;
  onDelete: () => Promise<void>;
}) {
  const [form, setForm] = useState<StructureWrite>(initial);
  const [error, setError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMode, setShareMode] = useState<'share' | 'quote'>('share');

  const setField = <K extends keyof StructureWrite>(k: K, v: StructureWrite[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const selectedType = structureTypes.find((t) => t.id === form.structureTypeId) ?? null;

  const grateStylesQuery = useQuery({
    queryKey: ['grate-styles', form.structureTypeId],
    queryFn: () => api.get<GrateStyle[]>(`/api/v1/grate-styles?structureTypeId=${form.structureTypeId}`),
    enabled: !!form.structureTypeId && !!selectedType?.isNyloplast,
  });

  const foundriesQuery = useQuery({
    queryKey: ['foundries'],
    queryFn: () => api.get<Foundry[]>('/api/v1/foundries'),
  });

  const foundryPartsQuery = useQuery({
    queryKey: ['foundry-parts', form.foundryId],
    queryFn: () => api.get<FoundryPart[]>(`/api/v1/foundries/${form.foundryId}/parts`),
    enabled: !!form.foundryId,
  });

  const lookupMutation = useMutation({
    mutationFn: () => {
      if (!form.structureTypeId) return Promise.resolve(null);
      return api.post<CastingMatch | null>('/api/v1/structures/casting-lookup', {
        structureTypeId: form.structureTypeId,
        permanent: form.permanent,
        oilFiltration: form.oilFiltration,
        extendedBottomHeavyDuty: form.extendedBottomHeavyDuty,
        grateStyleId: form.grateStyleId,
        clearOpeningLeftToRight: form.clearOpeningLeftToRight,
        clearOpeningFrontToBack: form.clearOpeningFrontToBack,
        clearOpeningLongSpan: form.clearOpeningLongSpan,
        clearOpeningShortSpan: form.clearOpeningShortSpan,
        frameClearOpeningDiameter: form.frameClearOpeningDiameter,
        curbOpeningWidth: form.curbOpeningWidth,
        basinSize: form.basinSize,
      });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (detail) return api.put<StructureDetail>(`/api/v1/structures/${detail.id}`, form);
      return api.post<StructureDetail>('/api/v1/structures', form);
    },
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof Error ? err.message : 'Save failed'),
  });

  const partNumber = lookupMutation.data
    ? form.permanent && form.oilFiltration
      ? lookupMutation.data.partNumberPermanentOilFilter
      : form.permanent
        ? lookupMutation.data.partNumberPermanentNoFilter
        : lookupMutation.data.partNumberTemporary
    : detail?.partNumber ?? null;

  return (
    <Shell>
      <div className="row mt-3 align-items-end">
        <div className="col-md-8">
          <Link to="/admin/structures" className="small text-muted">← Structures</Link>
          <h4 className="page-title mb-0">
            {detail ? detail.nickname || `Structure #${detail.id}` : 'New structure'}
          </h4>
        </div>
        <div className="col-md-4 text-md-end mt-2 mt-md-0 d-flex gap-2 justify-content-md-end">
          {detail && (
            <a
              href={`/api/v1/structures/${detail.id}/pdf`}
              target="_blank"
              rel="noopener"
              className="btn btn-dark"
            >
              Download PDF
            </a>
          )}
          {detail && <Button variant="danger" onClick={() => onDelete()}>Delete</Button>}
          <Button
            disabled={saveMutation.isPending || !form.structureTypeId}
            onClick={() => { setError(null); saveMutation.mutate(); }}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <ErrorAlert message={error} />

      <div className="row">
        <div className="col-12 col-xl-6">
        <Card title="Basics">
          <div>
            <Field label="Nickname">
              <TextInput
                value={form.nickname ?? ''}
                onChange={(e) => setField('nickname', e.target.value)}
              />
            </Field>
            <Field label="Structure type">
              <Select
                value={form.structureTypeId ?? ''}
                onChange={(e) => setField('structureTypeId', e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">— select —</option>
                {structureTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.family ? `${t.family}: ` : ''}{t.name}</option>
                ))}
              </Select>
            </Field>
            <div className="row g-3 mb-3">
              <BoolField
                label="Permanent"
                value={form.permanent}
                onChange={(v) => setField('permanent', v)}
              />
              <BoolField
                label="Oil filtration"
                value={form.oilFiltration}
                onChange={(v) => setField('oilFiltration', v)}
                disabled={!form.permanent}
              />
              <BoolField
                label="Heavy-duty bottom"
                value={form.extendedBottomHeavyDuty}
                onChange={(v) => setField('extendedBottomHeavyDuty', v)}
              />
              <BoolField
                label="Draft"
                value={form.isDraft}
                onChange={(v) => setField('isDraft', v)}
              />
            </div>
            <Field label="Notes">
              <TextArea
                rows={3}
                value={form.structureNotes ?? ''}
                onChange={(e) => setField('structureNotes', e.target.value)}
              />
            </Field>
          </div>
        </Card>
        </div>

        <div className="col-12 col-xl-6">
        <Card title="Dimensions" actions={selectedType ? <Pill tone="sky">{selectedType.family}</Pill> : null}>
          <DimensionFields type={selectedType} form={form} setField={setField} grateStyles={grateStylesQuery.data ?? []} />
        </Card>
        </div>

        <div className="col-12 col-xl-6">
        <Card
          title="Casting"
          actions={
            <Button
              variant="secondary"
              disabled={!form.structureTypeId || lookupMutation.isPending}
              onClick={() => lookupMutation.mutate()}
            >
              {lookupMutation.isPending ? 'Looking up…' : 'Re-run lookup'}
            </Button>
          }
        >
          <div>
            <p className="text-muted mb-1">
              Resolved part:{' '}
              <span className="font-monospace fw-semibold text-dark">{partNumber ?? '—'}</span>
            </p>
            {lookupMutation.data && (
              <p className="text-muted small mb-0">
                Part #{lookupMutation.data.partId} · inlet: {lookupMutation.data.inletTypeName ?? '—'}
              </p>
            )}
            {lookupMutation.isError && (
              <p className="text-danger small mb-0">Lookup failed.</p>
            )}
          </div>
        </Card>
        </div>

        <div className="col-12 col-xl-6">
        <Card title="Foundry override">
          <p className="text-muted small mb-3">
            Pick a foundry part directly to bypass the dimension-based lookup.
          </p>
          <div>
            <Field label="Foundry">
              <Select
                value={form.foundryId ?? ''}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : null;
                  setField('foundryId', v);
                  setField('foundryPartId', null);
                }}
              >
                <option value="">— none —</option>
                {(foundriesQuery.data ?? []).map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </Select>
            </Field>
            <Field label="Foundry part">
              <Select
                value={form.foundryPartId ?? ''}
                onChange={(e) => setField('foundryPartId', e.target.value ? Number(e.target.value) : null)}
                disabled={!form.foundryId}
              >
                <option value="">— none —</option>
                {(foundryPartsQuery.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.foundryPartNumber}</option>
                ))}
              </Select>
            </Field>
          </div>
        </Card>
        </div>
      </div>

      {detail && (
        <div className="row">
          <div className="col-12">
            <StructurePhotosCard structureId={detail.id} />
          </div>
        </div>
      )}
      {detail && (
        <div className="row">
          <div className="col-12">
            <Card title="Share structure">
              <p className="text-muted">
                Share or submit this structure by email, or send it to ADS for quoting.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  onClick={() => {
                    setShareMode('quote');
                    setShareOpen(true);
                  }}
                >
                  Request A Quote
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    setShareMode('share');
                    setShareOpen(true);
                  }}
                >
                  Share Or Submit
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
      {detail && (
        <div className="row">
          <div className="col-12">
            <AdminAgentNotesCard kind="structures" id={detail.id} initialQuoteCompleted={detail.quoteCompleted} />
          </div>
        </div>
      )}

      {detail && shareOpen && (
        <ShareStructureModal
          structureId={detail.id}
          ownerEmail={null}
          mode={shareMode}
          onClose={() => setShareOpen(false)}
        />
      )}
    </Shell>
  );
}

function BoolField({
  label, value, onChange, disabled,
}: { label: string; value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  const id = `bool-${label.replace(/\s+/g, '-')}`;
  return (
    <div className="col-6">
      <div className={`form-check ${disabled ? 'opacity-50' : ''}`}>
        <input
          id={id}
          type="checkbox"
          className="form-check-input"
          checked={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <label className="form-check-label" htmlFor={id}>{label}</label>
      </div>
    </div>
  );
}

function DimensionFields({
  type, form, setField, grateStyles,
}: {
  type: StructureType | null;
  form: StructureWrite;
  setField: <K extends keyof StructureWrite>(k: K, v: StructureWrite[K]) => void;
  grateStyles: GrateStyle[];
}) {
  if (!type) return <p className="text-muted">Pick a structure type first.</p>;

  const numField = (label: string, key: keyof StructureWrite) => (
    <Field label={label}>
      <NumberInput
        value={(form[key] as number | null | undefined) ?? ''}
        onChange={(e) => setField(key, (e.target.value === '' ? null : Number(e.target.value)) as StructureWrite[typeof key])}
      />
    </Field>
  );

  const cell = (node: ReactNode) => <div className="col-md-6">{node}</div>;
  if (type.is4SeatSquare || type.isCastIronCurbBoxCombo || type.isCastIronRectangular) {
    return (
      <div className="row g-3">
        {cell(numField('Grate L→R', 'grateSizeLeftToRight'))}
        {cell(numField('Grate F→B', 'grateSizeFrontToBack'))}
        {cell(numField('Clear opening L→R', 'clearOpeningLeftToRight'))}
        {cell(numField('Clear opening F→B', 'clearOpeningFrontToBack'))}
        {cell(numField('Ext. bottom depth', 'extendedBottomDepth'))}
        {cell(numField('Ext. bottom width', 'extendedBottomWidth'))}
      </div>
    );
  }
  if (type.is2SeatLong || type.is2SeatShort) {
    return (
      <div className="row g-3">
        {cell(numField('Grate long span', 'grateSizeLongSpan'))}
        {cell(numField('Grate short span', 'grateSizeShortSpan'))}
        {cell(numField('Clear opening long span', 'clearOpeningLongSpan'))}
        {cell(numField('Clear opening short span', 'clearOpeningShortSpan'))}
      </div>
    );
  }
  if (type.isCastIronRound) {
    return (
      <div className="row g-3">
        {cell(numField('Grate diameter', 'grateDiameter'))}
        {cell(numField('Frame clear opening diameter', 'frameClearOpeningDiameter'))}
      </div>
    );
  }
  if (type.isOpenThroat) {
    return (
      <div className="row g-3">
        {cell(numField('Curb opening width', 'curbOpeningWidth'))}
        {cell(numField('Catch basin wall-to-wall', 'catchBasinWallToWallInsideDimension'))}
      </div>
    );
  }
  if (type.isNyloplast) {
    return (
      <div className="row g-3">
        {cell(numField('Basin size', 'basinSize'))}
        <div className="col-md-6"><Field label="Grate style">
          <Select
            value={form.grateStyleId ?? ''}
            onChange={(e) => setField('grateStyleId', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— select —</option>
            {grateStyles.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </Select>
        </Field></div>
      </div>
    );
  }
  return <p className="text-muted">No dimension fields configured for this type.</p>;
}

function Shell({ children }: { children: ReactNode }) {
  return <div className="container-fluid" data-unsaved-guard>{children}</div>;
}
